/**
 * AMOXTLI - Application Router & Entry Point
 * 
 * Propiedad Intelectual: Soluciones TecnoExpertas SAPI de CV
 * Autor: Luis Alberto Orozco Arias
 * Licencia: Propietaria - Todos los derechos reservados © 2026
 * 
 * Descripción: Enrutamiento principal y configuración de la aplicación
 */

import React, { Suspense, lazy, useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'

import Layout from './components/Layout'
import { authService } from './services/api'
import { useStore } from './services/store'
import { ThemeProvider } from './services/ThemeContext'

const LAZY_RELOAD_KEY = 'amoxtli-lazy-reload-attempted'

function lazyPage(importer) {
  return lazy(async () => {
    try {
      const module = await importer()
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(LAZY_RELOAD_KEY)
      }
      return module
    } catch (error) {
      const message = String(error?.message || error || '')
      const chunkLoadFailed = /Failed to fetch dynamically imported module|Importing a module script failed|Expected a JavaScript-or-Wasm module script/i.test(message)

      if (typeof window !== 'undefined' && chunkLoadFailed && !sessionStorage.getItem(LAZY_RELOAD_KEY)) {
        sessionStorage.setItem(LAZY_RELOAD_KEY, '1')
        window.location.reload()
        return new Promise(() => {})
      }

      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(LAZY_RELOAD_KEY)
      }

      throw error
    }
  })
}

const LandingPage = lazyPage(() => import('./pages/LandingPage'))
const LoginPage = lazyPage(() => import('./pages/LoginPage'))
const PrivacyPage = lazyPage(() => import('./pages/PrivacyPage'))
const TermsPage = lazyPage(() => import('./pages/TermsPage'))
const CookiesPage = lazyPage(() => import('./pages/CookiesPage'))
const ContactPage = lazyPage(() => import('./pages/ContactPage'))
const PortalClientePage = lazyPage(() => import('./pages/PortalClientePage').then(m => ({ default: m.PortalClientePage })))
const SecurityPage = lazyPage(() => import('./pages/SecurityPage'))
const HelpPage = lazyPage(() => import('./pages/HelpPage'))
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'))
const EmpresasPage = lazyPage(() => import('./pages/EmpresasPage'))
const FacturasPage = lazyPage(() => import('./pages/FacturasPage'))
const DeclaracionesPage = lazyPage(() => import('./pages/DeclaracionesPage'))
const PagosPage = lazyPage(() => import('./pages/PagosPage'))
const ReportesPage = lazyPage(() => import('./pages/ReportesPage'))
const PerfilPage = lazyPage(() => import('./pages/PerfilPage'))
const AdminUsuariosPage = lazyPage(() => import('./pages/AdminUsuariosPage'))
const ProveedoresPage = lazyPage(() => import('./pages/ProveedoresPage'))
const ClientesPage = lazyPage(() => import('./pages/ClientesPage'))
const SuscripcionesPage = lazyPage(() => import('./pages/SuscripcionesPage'))
const LicenciasPage = lazyPage(() => import('./pages/LicenciasPage'))
const FacturasDescargaPage = lazyPage(() => import('./pages/FacturasDescargaPage'))
const PlantillasPdfPage = lazyPage(() => import('./pages/PlantillasPdfPage'))
const AjustesPage = lazyPage(() => import('./pages/ConfiguracionPage'))
const PersonalizacionPage = lazyPage(() => import('./pages/PersonalizacionPage'))
const AdminPlanesPage = lazyPage(() => import('./pages/AdminPlanesPage'))
const CobrosHubPage = lazyPage(() => import('./pages/CobrosHubPage'))
const MiCuentaPage = lazyPage(() => import('./pages/MiCuentaPage'))

function ProtectedLayout({ children }) {
  const isAuthenticated = !!localStorage.getItem('access_token')
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return <Layout>{children}</Layout>
}

function App() {
  const [authChecked, setAuthChecked] = useState(false)
  const setUser = useStore((state) => state.setUser)

  useEffect(() => {
    const hydrateUser = async () => {
      const token = localStorage.getItem('access_token')
      if (!token) {
        setUser(null)
        setAuthChecked(true)
        return
      }

      try {
        const res = await authService.getCurrentUser()
        setUser(res.data)
      } catch (_err) {
        localStorage.removeItem('access_token')
        setUser(null)
      } finally {
        setAuthChecked(true)
      }
    }

    hydrateUser()
  }, [setUser])

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Cargando sesión...
      </div>
    )
  }

  return (
    <HelmetProvider>
      <ThemeProvider>
        <Suspense
          fallback={(
            <div className="min-h-screen flex items-center justify-center text-gray-600">
              Cargando modulo...
            </div>
          )}
        >
          <Routes>
        <Route
          path="/"
          element={
            !!localStorage.getItem('access_token') ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LandingPage />
            )
          }
        />
        <Route path="/inicio" element={<LandingPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route
          path="/login"
          element={
            !!localStorage.getItem('access_token') ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LoginPage />
            )
          }
        />
        {/* Páginas Públicas del Footer */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/ayuda" element={<HelpPage />} />
        {/* Fin de Páginas Públicas */}
        <Route path="/portal/cliente/:token" element={<PortalClientePage />} />
        <Route
          path="/dashboard"
          element={(
            <ProtectedLayout>
              <DashboardPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/ayuda-centro"
          element={<Navigate to="/ayuda" replace />}
        />
        <Route
          path="/empresas"
          element={(
            <ProtectedLayout>
              <EmpresasPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/facturas"
          element={(
            <ProtectedLayout>
              <FacturasPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/clientes"
          element={(
            <ProtectedLayout>
              <ClientesPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/cobros"
          element={(
            <ProtectedLayout>
              <CobrosHubPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/suscripciones"
          element={(
            <ProtectedLayout>
              <SuscripcionesPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/declaraciones"
          element={(
            <ProtectedLayout>
              <DeclaracionesPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/pagos"
          element={(
            <ProtectedLayout>
              <PagosPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/reportes"
          element={(
            <ProtectedLayout>
              <ReportesPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/mi-cuenta"
          element={(
            <ProtectedLayout>
              <MiCuentaPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/perfil"
          element={(
            <ProtectedLayout>
              <PerfilPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/admin/usuarios"
          element={(
            <ProtectedLayout>
              <AdminUsuariosPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/proveedores"
          element={(
            <ProtectedLayout>
              <ProveedoresPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/licencias"
          element={(
            <ProtectedLayout>
              <LicenciasPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/facturas-descarga"
          element={(
            <ProtectedLayout>
              <FacturasDescargaPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/plantillas-pdf"
          element={(
            <ProtectedLayout>
              <PlantillasPdfPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/ajustes"
          element={(
            <ProtectedLayout>
              <AjustesPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/personalizacion"
          element={(
            <ProtectedLayout>
              <PersonalizacionPage />
            </ProtectedLayout>
          )}
        />
        <Route
          path="/admin/planes"
          element={(
            <ProtectedLayout>
              <AdminPlanesPage />
            </ProtectedLayout>
          )}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ThemeProvider>
    </HelmetProvider>
  )
}

export default App
